import { useEffect, useState } from "react";

import { Check, ChevronsUpDown, Loader2, Package } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useProductSearch } from "~/hooks/wms/useStockConsultation";
import { cn } from "~/lib/utils";

interface ProductSearchSelectProps {
  value: string;
  selectedLabel?: string;
  onSelect: (product: { id: string; name: string }) => void;
  disabled?: boolean;
}

// Debounce a value so we hit the search RPC once the user pauses typing.
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// Server-backed product picker (search_products_for_stock). Used to pick a
// finished product before choosing its lote in the PT transfer form.
export function ProductSearchSelect({
  value,
  selectedLabel,
  onSelect,
  disabled,
}: ProductSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isFetching } = useProductSearch(debouncedSearch);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-full justify-between font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value && selectedLabel ? selectedLabel : "Buscar producto…"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        // The parent "Nuevo Traslado" is a modal Radix Dialog, which sets
        // pointer-events:none on <body>; the portaled popover inherits it and
        // item clicks get swallowed as outside-dismisses. Re-assert clickability
        // and lift above the dialog (mirrors ClientSearchSelect).
        style={{ pointerEvents: "auto", zIndex: 99999 }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Nombre, SKU o código de barras…"
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <CommandList>
            <CommandEmpty className="px-4 py-3 text-sm text-muted-foreground">
              {isFetching ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando…
                </span>
              ) : search.length < 2 ? (
                "Escribe al menos 2 caracteres para buscar"
              ) : (
                "No se encontraron productos"
              )}
            </CommandEmpty>
            <CommandGroup>
              {results.map((p) => (
                <CommandItem
                  key={p.product_id}
                  value={p.product_id}
                  onSelect={() => {
                    onSelect({ id: p.product_id, name: p.product_name });
                    setOpen(false);
                    setSearch("");
                  }}
                  className="flex items-start gap-2"
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value === p.product_id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <Package className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="truncate">{p.product_name}</span>
                    {p.sku && (
                      <span className="truncate text-xs text-muted-foreground">
                        SKU: {p.sku}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
